import { isCourseItem, type CartItem } from "./stores/cart";
import { purchaseDoseSelection } from "./purchaseRequest";

export interface CartPromotionLine {
  key: string;
  gross_cents: number;
  discount_cents: number;
  total_cents: number;
  recurring_cents: number;
  renewal_amount_cents?: number;
  discount_duration?: "once" | "forever" | "repeating";
  discount_duration_months?: number | null;
}
export interface CartPromotionQuote {
  id: string;
  code: string;
  subtotal_cents: number;
  discount_cents: number;
  total_cents: number;
  items: CartPromotionLine[];
}
export interface CartPromotionRequest {
  lab_acquisition_id?: string;
  request_id: string;
  code: string;
  payment_method_id: string;
  items: ReturnType<typeof promotionItems>;
}
export interface SavedCartPromotion {
  quote: CartPromotionQuote;
  items: CartItem[];
  paymentMethodId: string;
  started: boolean;
  compoundRequestId: string;
  compoundPending?: boolean;
  /** Compound lines of this request paid before a later line failed. */
  paidProductIds?: string[];
}
export const promotionLineKey = (item: CartItem) => `${item.origin}:${item.id}`;
export function promotionItems(items: CartItem[]) {
  if (items.some((item) => item.origin === "protocol"))
    throw new Error(
      "Check out this plan separately before applying a blood test promotion.",
    );
  if (items.some(isCourseItem))
    throw new Error(
      "Check out the one-time course separately before applying a blood test promotion.",
    );
  if (items.some((item) => item.stack_id))
    throw new Error(
      "Check out the stack separately before applying a blood test promotion.",
    );
  return items.map((item) => ({
    key: promotionLineKey(item),
    kind:
      item.origin === "lab"
        ? ("lab" as const)
        : item.origin === "supplement"
          ? ("supplement" as const)
          : ("compound" as const),
    product_id: item.id,
    expected_amount_cents: item.price_cents * item.quantity,
    quantity: item.quantity,
    ...(item.origin === "lab"
      ? {
          purchase_type: item.lab_purchase_type ?? "subscription",
          interval_months:
            item.lab_purchase_type === "one_time"
              ? null
              : (item.lab_interval_months ?? 1),
        }
      : {}),
    ...(item.origin === "product"
      ? {
          frequency: item.frequency ?? "monthly",
          ...purchaseDoseSelection(item),
        }
      : {}),
  }));
}
export function promotionCartMatches(
  saved: SavedCartPromotion,
  items: CartItem[],
  paymentMethodId: string | null,
) {
  // Lines a promotion never covers (protocols, courses, stacks) mean the cart
  // no longer matches the quote.
  const unpromotable = (item: CartItem) =>
    item.origin === "protocol" || isCourseItem(item) || Boolean(item.stack_id);
  if (
    saved.paymentMethodId !== paymentMethodId ||
    items.some(unpromotable) ||
    saved.items.some(unpromotable)
  )
    return false;
  const original = promotionItems(saved.items);
  const next = promotionItems(items);
  // Once started, paid lines leave the cart. Remaining items must retain their
  // original terms and allocation; new lines need a separate confirmation.
  return saved.started
    ? next.every((item) =>
        original.some((line) => JSON.stringify(line) === JSON.stringify(item)),
      )
    : JSON.stringify(original) === JSON.stringify(next);
}
export function remainingPromotion(
  saved: SavedCartPromotion,
  items: CartItem[],
) {
  const keys = new Set(items.map(promotionLineKey));
  return saved.quote.items.filter((line) => keys.has(line.key));
}
const storageKey = (owner: string) =>
  `system.portal.cart-promotion.${owner}.v1`;
export function readCartPromotion(owner?: string): SavedCartPromotion | null {
  if (!owner) return null;
  try {
    const value = JSON.parse(localStorage.getItem(storageKey(owner)) || "null");
    return value?.quote?.id &&
      Array.isArray(value.quote.items) &&
      Array.isArray(value.items) &&
      value.paymentMethodId &&
      value.compoundRequestId
      ? value
      : null;
  } catch {
    return null;
  }
}
export function saveCartPromotion(
  owner: string,
  value: SavedCartPromotion | null,
) {
  if (value) localStorage.setItem(storageKey(owner), JSON.stringify(value));
  else localStorage.removeItem(storageKey(owner));
}
export function assertPromotionQuote(
  quote: CartPromotionQuote,
  items: CartItem[],
) {
  const request = promotionItems(items);
  if (
    !quote.id ||
    quote.items.length !== request.length ||
    request.some((item) => {
      const lines = quote.items.filter((line) => line.key === item.key);
      const line = lines[0];
      return (
        lines.length !== 1 ||
        line.gross_cents !== item.expected_amount_cents ||
        !Number.isSafeInteger(line.total_cents) ||
        line.total_cents < 0 ||
        line.discount_cents < 0 ||
        line.total_cents + line.discount_cents !== line.gross_cents
      );
    }) ||
    quote.subtotal_cents !==
      quote.items.reduce((sum, line) => sum + line.gross_cents, 0) ||
    quote.discount_cents !==
      quote.items.reduce((sum, line) => sum + line.discount_cents, 0) ||
    quote.total_cents !== quote.subtotal_cents - quote.discount_cents
  ) {
    throw new Error(
      "The promotion could not be confirmed for these items. Apply the code again before purchasing.",
    );
  }
}
